import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";
import "../assets/CSS/Dashboard.css";

export default function BoxPartyWise() {
  const API_BASE_URL = axiosInstance.defaults.baseURLPath || "";

  /* ================= USER ================= */

  const user = JSON.parse(localStorage.getItem("user")) || {};

  const userId = String(
    user?.EmployeeID ||
    user?.id ||
    user?.UserID ||
    ""
  );

  const role = String(user?.Auth || "").trim();
  const normalizedRole = role.toLowerCase();

  const isKalim =
    userId === "1777" &&
    normalizedRole === "admin";

  const isSwapnil =
    userId === "11" ||
    normalizedRole === "supervisor";

  const isVendor = normalizedRole === "vendor";

  const isAdmin =
    ["admin", "superadmin"].includes(normalizedRole) ||
    userId === "1021";

  const canSeeBrand = isKalim || isSwapnil || isAdmin;
  const canSeeKalim = isKalim || isAdmin;
  const canSeeSupervisor = isSwapnil || isAdmin;
  const canSeeVendor = isVendor || isAdmin;

  /* ================= SIDEBAR ================= */

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const toggleSidebar = () => {
    setIsSidebarVisible((previous) => !previous);
  };

  /* ================= ACTIVE TAB ================= */

  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    if (isAdmin) {
      setActiveTab("brand");
    } else if (isKalim) {
      setActiveTab("kalim");
    } else if (isSwapnil) {
      setActiveTab("supervisor");
    } else {
      setActiveTab("vendor");
    }
  }, [isAdmin, isKalim, isSwapnil]);

  /* ================= BRAND DATA ================= */

  const [records, setRecords] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= TRACKING DATA ================= */

  const [kalimList, setKalimList] = useState([]);
  const [supervisorList, setSupervisorList] = useState([]);
  const [vendorList, setVendorList] = useState([]);

  const [kalimSearch, setKalimSearch] = useState("");
  const [kalimStatusFilter, setKalimStatusFilter] = useState("");
  const [supervisorSearch, setSupervisorSearch] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorStatusFilter, setVendorStatusFilter] = useState("");

  const [trackingLoading, setTrackingLoading] = useState(false);

  /* ================= KPI ================= */

  const [kpi, setKpi] = useState({
    total: 0,
    images: 0
  });

  /* ================= BRAND MODALS ================= */

  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");

  const [saving, setSaving] = useState(false);

  /* ================= BRAND IMAGE STATE ================= */

  const MAX_IMAGES = 25;

  const [dragActive, setDragActive] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    newImages: []
  });

  const [existingImages, setExistingImages] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  /* ================= ASSIGN VENDOR MODAL ================= */

  const initialAssignData = {
    lotId: "",
    quantity: "",
    vendorId: "",
    vendorName: "",
    dueDate: "",
    notes: "",
    photoSource: "GROUP",
    photoGroupId: "",
    images: []
  };

  const [assignModal, setAssignModal] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const [assignData, setAssignData] = useState(initialAssignData);
  const [selectedGroupImages, setSelectedGroupImages] = useState([]);
  const [groupImagesLoading, setGroupImagesLoading] = useState(false);

  /* ================= HELPERS ================= */

  const getErrorMessage = (error, fallback = "Something went wrong") => {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      fallback
    );
  };

  const normalizeStatus = (status) => {
    return String(status || "").trim().toUpperCase();
  };

  const buildImageUrl = (imagePath) => {
    if (!imagePath) return "";

    const cleanPath = String(imagePath)
      .replace(/\\/g, "/")
      .replace(/^\/+/, "");

    const cleanBaseUrl = String(API_BASE_URL).replace(/\/+$/, "");

    return `${cleanBaseUrl}/${cleanPath}`;
  };

  const parsePhotos = (samplePhotos) => {
    if (!samplePhotos) return [];

    if (Array.isArray(samplePhotos)) {
      return samplePhotos;
    }

    try {
      const parsed = JSON.parse(samplePhotos);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const filterData = (data, searchText) => {
    const keyword = String(searchText || "").trim().toLowerCase();

    if (!keyword) return data;

    return data.filter((item) =>
      Object.values(item || {}).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(keyword)
      )
    );
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("en-IN");
  };

  const getMinimumDueDate = () => {
    const today = new Date();

    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  /* ================= FILTERED DATA ================= */

  const uniqueKalimStatuses = useMemo(() => {
    const statuses = new Set(kalimList.map(item => normalizeStatus(item.Status)));
    return Array.from(statuses).sort();
  }, [kalimList]);

  const uniqueVendorStatuses = useMemo(() => {
    const statuses = new Set(vendorList.map(item => normalizeStatus(item.Status)));
    return Array.from(statuses).sort();
  }, [vendorList]);

  const filteredKalim = useMemo(() => {
    let data = filterData(kalimList, kalimSearch);
    if (kalimStatusFilter) {
      data = data.filter(item => normalizeStatus(item.Status) === kalimStatusFilter);
    }
    return data;
  }, [kalimList, kalimSearch, kalimStatusFilter]);

  const filteredSupervisor = useMemo(
    () => filterData(supervisorList, supervisorSearch),
    [supervisorList, supervisorSearch]
  );

  const filteredVendor = useMemo(() => {
    let data = filterData(vendorList, vendorSearch);
    if (vendorStatusFilter) {
      data = data.filter(item => normalizeStatus(item.Status) === vendorStatusFilter);
    }
    return data;
  }, [vendorList, vendorSearch, vendorStatusFilter]);

  /* ================= BRAND FETCH ================= */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axiosInstance.get("/api/box-party", {
        params: {
          page,
          limit: perPage,
          search
        }
      });

      setRecords(response.data?.data || []);

      setTotalRows(
        Number(
          response.data?.total ??
          response.data?.pagination?.total ??
          0
        )
      );
    } catch (error) {
      setRecords([]);
      setTotalRows(0);

      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to fetch brand library"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search]);

  const loadBrandOptions = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/box-party", {
        params: {
          page: 1,
          limit: 100,
          search: ""
        }
      });

      setBrandOptions(response.data?.data || []);
    } catch (error) {
      console.error("Brand options error:", error);
      setBrandOptions([]);
    }
  }, []);

  const loadKpi = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/box-party/kpi");

      setKpi({
        total: Number(response.data?.TotalParties || 0),
        images: Number(response.data?.TotalImages || 0)
      });
    } catch (error) {
      console.error("KPI error:", error);
    }
  }, []);

  /* ================= TRACKING FETCH ================= */

  const loadKalim = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/lot/kalim");
      setKalimList(response.data?.data || []);
    } catch (error) {
      console.error("Kalim list error:", error);
      setKalimList([]);
    }
  }, []);

  const loadSupervisor = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/lot/swapnil");
      setSupervisorList(response.data?.data || []);
    } catch (error) {
      console.error("Supervisor list error:", error);
      setSupervisorList([]);
    }
  }, []);

  const loadVendor = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/lot/vendor");
      setVendorList(response.data?.data || []);
    } catch (error) {
      console.error("Vendor list error:", error);
      setVendorList([]);
    }
  }, []);

  const refreshTrackingLists = useCallback(async (showSpinner = true) => {
    try {
      if (showSpinner) {
        setTrackingLoading(true);
      }

      await Promise.all([
        loadKalim(),
        loadSupervisor(),
        loadVendor()
      ]);
    } finally {
      if (showSpinner) {
        setTrackingLoading(false);
      }
    }
  }, [loadKalim, loadSupervisor, loadVendor]);

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    loadKpi();
    loadBrandOptions();
    refreshTrackingLists();
  }, [loadKpi, loadBrandOptions, refreshTrackingLists]);

  /* ================= AUTO REFRESH ================= */

  useEffect(() => {
    const interval = setInterval(() => {
      // background sync without spinner
      refreshTrackingLists(false);
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshTrackingLists]);

  /* ================= BRAND IMAGE HANDLING ================= */

  const handleImageChange = (files) => {
    const newFiles = Array.from(files || []);

    if (
      existingImages.length +
      formData.newImages.length +
      newFiles.length >
      MAX_IMAGES
    ) {
      Swal.fire(
        "Limit",
        `Maximum ${MAX_IMAGES} images are allowed`,
        "warning"
      );

      return;
    }

    setFormData((previous) => ({
      ...previous,
      newImages: [
        ...previous.newImages,
        ...newFiles
      ]
    }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    handleImageChange(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const removeNewImage = (index) => {
    setFormData((previous) => ({
      ...previous,
      newImages: previous.newImages.filter(
        (_, imageIndex) => imageIndex !== index
      )
    }));
  };

  const removeExistingImage = (image) => {
    setExistingImages((previous) =>
      previous.filter(
        (item) => item.ImageID !== image.ImageID
      )
    );

    setDeletedImages((previous) => [
      ...previous,
      image.ImageID
    ]);
  };

  /* ================= BRAND SAVE ================= */

  const resetForm = () => {
    setShowModal(false);
    setEditMode(false);
    setEditingId(null);
    setSelectedTitle("");

    setFormData({
      title: "",
      newImages: []
    });

    setExistingImages([]);
    setDeletedImages([]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving) return;

    const title = String(formData.title || "").trim();

    if (!title) {
      return Swal.fire(
        "Validation",
        "Brand name is required",
        "warning"
      );
    }

    try {
      setSaving(true);

      const data = new FormData();

      data.append("title", title);
      data.append(
        "deletedImages",
        JSON.stringify(deletedImages)
      );

      formData.newImages.forEach((image) => {
        data.append("images", image);
      });

      if (editMode) {
        await axiosInstance.put(
          `/api/box-party/${editingId}`,
          data
        );
      } else {
        await axiosInstance.post(
          "/api/box-party",
          data
        );
      }

      await Swal.fire(
        "Success",
        editMode
          ? "Brand updated successfully"
          : "Brand created successfully",
        "success"
      );

      resetForm();

      await Promise.all([
        fetchData(),
        loadBrandOptions(),
        loadKpi()
      ]);
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to save brand"),
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  /* ================= BRAND EDIT ================= */

  const handleEdit = async (partyId) => {
    try {
      const response = await axiosInstance.get(
        `/api/box-party/${partyId}`
      );

      if (!response.data?.party) {
        throw new Error("Brand record not found");
      }

      setEditMode(true);
      setEditingId(partyId);
      setSelectedTitle(response.data.party.Title || "");

      setFormData({
        title: response.data.party.Title || "",
        newImages: []
      });

      setExistingImages(response.data.images || []);
      setDeletedImages([]);
      setShowModal(true);
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to load brand"),
        "error"
      );
    }
  };

  /* ================= BRAND ARCHIVE ================= */

  const handleArchive = async (row) => {
    const confirmation = await Swal.fire({
      title: "Archive Photo Group?",
      text: `${row.Title} will be removed from the active brand library.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, archive",
      cancelButtonText: "Cancel"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axiosInstance.delete(
        `/api/box-party/${row.PartyID}`
      );

      await Swal.fire(
        "Archived",
        "Photo group archived successfully",
        "success"
      );

      await Promise.all([
        fetchData(),
        loadBrandOptions(),
        loadKpi()
      ]);
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to archive photo group"),
        "error"
      );
    }
  };

  /* ================= VIEW BRAND IMAGES ================= */

  const handleViewImages = async (partyId) => {
    try {
      const response = await axiosInstance.get(
        `/api/box-party/${partyId}`
      );

      setSelectedImages(response.data?.images || []);
      setSelectedTitle(response.data?.party?.Title || "");
      setShowImageModal(true);
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to load images"),
        "error"
      );
    }
  };

  /* ================= ASSIGN VENDOR ================= */

  const closeAssignModal = () => {
    if (assignSaving) return;

    setAssignModal(false);
    setAssignData(initialAssignData);
    setSelectedGroupImages([]);
  };

  const openAssignModal = async (row) => {
    setAssignData({
      ...initialAssignData,
      lotId: row.LotID || ""
    });

    setSelectedGroupImages([]);
    setAssignModal(true);

    if (!brandOptions.length) {
      await loadBrandOptions();
    }
  };

  const handleAssignImages = (files) => {
    const selectedFiles = Array.from(files || []);

    setAssignData((previous) => {
      const remainingSlots =
        MAX_IMAGES - previous.images.length;

      const acceptedFiles = selectedFiles.slice(
        0,
        Math.max(remainingSlots, 0)
      );

      if (selectedFiles.length > remainingSlots) {
        Swal.fire(
          "Limit",
          `Maximum ${MAX_IMAGES} images are allowed`,
          "warning"
        );
      }

      return {
        ...previous,
        images: [
          ...previous.images,
          ...acceptedFiles
        ]
      };
    });
  };

  const removeAssignImage = (index) => {
    setAssignData((previous) => ({
      ...previous,
      images: previous.images.filter(
        (_, imageIndex) => imageIndex !== index
      )
    }));
  };

  const handlePhotoSourceChange = (source) => {
    setAssignData((previous) => ({
      ...previous,
      photoSource: source,
      photoGroupId: source === "GROUP"
        ? previous.photoGroupId
        : "",
      images: source === "UPLOAD"
        ? previous.images
        : []
    }));

    if (source === "UPLOAD") {
      setSelectedGroupImages([]);
    }
  };

  const handlePhotoGroupChange = async (partyId) => {
    setAssignData((previous) => ({
      ...previous,
      photoGroupId: partyId
    }));

    setSelectedGroupImages([]);

    if (!partyId) return;

    try {
      setGroupImagesLoading(true);

      const response = await axiosInstance.get(
        `/api/box-party/${partyId}`
      );

      setSelectedGroupImages(
        response.data?.images || []
      );
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(
          error,
          "Unable to load selected photo group"
        ),
        "error"
      );
    } finally {
      setGroupImagesLoading(false);
    }
  };

  const submitAssignVendor = async () => {
    if (assignSaving) return;

    const quantity = Number(assignData.quantity);

    if (!assignData.lotId.trim()) {
      return Swal.fire(
        "Validation",
        "Lot ID is required",
        "warning"
      );
    }

    if (!Number.isInteger(quantity) || quantity <= 0) {
      return Swal.fire(
        "Validation",
        "Quantity must be a positive whole number",
        "warning"
      );
    }

    if (
      !assignData.vendorId.trim() ||
      !assignData.vendorName.trim()
    ) {
      return Swal.fire(
        "Validation",
        "Vendor ID and Vendor Name are required",
        "warning"
      );
    }

    if (!assignData.dueDate) {
      return Swal.fire(
        "Validation",
        "Due date is required",
        "warning"
      );
    }

    if (
      assignData.photoSource === "GROUP" &&
      !assignData.photoGroupId
    ) {
      return Swal.fire(
        "Validation",
        "Select a saved photo group",
        "warning"
      );
    }

    if (
      assignData.photoSource === "UPLOAD" &&
      assignData.images.length === 0
    ) {
      return Swal.fire(
        "Validation",
        "Upload at least one sample photo",
        "warning"
      );
    }

    try {
      setAssignSaving(true);

      const data = new FormData();

      data.append("lotId", assignData.lotId.trim());
      data.append("quantity", String(quantity));
      data.append("vendorId", assignData.vendorId.trim());
      data.append("vendorName", assignData.vendorName.trim());
      data.append("dueDate", assignData.dueDate);
      data.append("notes", assignData.notes.trim());
      data.append("photoSource", assignData.photoSource);

      if (assignData.photoSource === "GROUP") {
        data.append(
          "photoGroupId",
          assignData.photoGroupId
        );
      }

      if (assignData.photoSource === "UPLOAD") {
        assignData.images.forEach((image) => {
          data.append("images", image);
        });
      }

      const response = await axiosInstance.post(
        "/api/lot/assign-vendor",
        data
      );

      await Swal.fire(
        "Success",
        response.data?.message ||
        "Vendor assigned successfully",
        "success"
      );

      closeAssignModal();
      await refreshTrackingLists();
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to assign vendor"),
        "error"
      );
    } finally {
      setAssignSaving(false);
    }
  };

  /* ================= PO STATUS ================= */

  const raisePurchaseOrder = async (lotId) => {
    const confirmation = await Swal.fire({
      title: "Mark PO Raised?",
      text: `Lot ${lotId} will be marked as PO RAISED.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update"
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axiosInstance.post("/api/lot/po-raised", {
        lotId
      });

      await Swal.fire(
        "Success",
        "PO Raised successfully",
        "success"
      );

      await refreshTrackingLists();
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to update PO"),
        "error"
      );
    }
  };

  /* ================= VENDOR STATUS ================= */

  const updateVendorStatus = async (row, nextStatus) => {
    const confirmation = await Swal.fire({
      title: `Change status to ${nextStatus}?`,
      text: `Lot: ${row.LotID}`,
      icon: "question",
      input: "textarea",
      inputLabel: "Remarks",
      inputPlaceholder: "Optional remarks",
      showCancelButton: true,
      confirmButtonText: "Update Status"
    });

    if (!confirmation.isConfirmed) return;

    try {
      const response = await axiosInstance.patch(
        `/api/lot/${encodeURIComponent(row.LotID)}/status`,
        {
          status: nextStatus,
          remarks: confirmation.value || ""
        }
      );

      await Swal.fire(
        "Success",
        response.data?.message || "Status updated",
        "success"
      );

      await refreshTrackingLists();
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to update status"),
        "error"
      );
    }
  };

  /* ================= SUPERVISOR RECEIVE ================= */

  const receiveOrder = async (row) => {
    const result = await Swal.fire({
      title: "Receive Vendor Delivery",
      html: `
        <div class="text-start">
          <div><strong>Lot:</strong> ${row.LotID || "-"}</div>
          <div><strong>Required Qty:</strong> ${row.VendorQty || "-"}</div>
        </div>
      `,
      input: "number",
      inputLabel: "Received Quantity",
      inputValue: row.VendorQty || "",
      inputAttributes: {
        min: "1",
        step: "1"
      },
      showCancelButton: true,
      confirmButtonText: "Receive Delivery",
      inputValidator: (value) => {
        const quantity = Number(value);

        if (!Number.isInteger(quantity) || quantity <= 0) {
          return "Enter a valid whole quantity";
        }

        if (quantity !== Number(row.VendorQty || 0)) {
          return "Received quantity must match required quantity";
        }

        return undefined;
      }
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axiosInstance.post(
        `/api/lot/${encodeURIComponent(row.LotID)}/receive`,
        {
          receivedQty: Number(result.value),
          remarks: `Received by ${userId}`
        }
      );

      await Swal.fire(
        "Success",
        response.data?.message || "Delivery received",
        "success"
      );

      await refreshTrackingLists();
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to receive delivery"),
        "error"
      );
    }
  };

  /* ================= SUPERVISOR INSPECTION ================= */

  const inspectOrder = async (row) => {
    const decision = await Swal.fire({
      title: "Inspect Vendor Order",
      text: `Lot: ${row.LotID}`,
      icon: "question",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Accept",
      denyButtonText: "Reject",
      cancelButtonText: "Cancel"
    });

    if (decision.isDismissed) return;

    if (decision.isConfirmed) {
      const remarksResult = await Swal.fire({
        title: "Accept Order",
        input: "textarea",
        inputLabel: "Inspection remarks",
        inputPlaceholder: "Optional remarks",
        showCancelButton: true,
        confirmButtonText: "Complete Order"
      });

      if (!remarksResult.isConfirmed) return;

      try {
        const response = await axiosInstance.post(
          `/api/lot/${encodeURIComponent(row.LotID)}/inspection`,
          {
            result: "ACCEPTED",
            remarks: remarksResult.value || ""
          }
        );

        await Swal.fire(
          "Completed",
          response.data?.message || "Order completed",
          "success"
        );

        await refreshTrackingLists();
      } catch (error) {
        Swal.fire(
          "Error",
          getErrorMessage(error, "Inspection failed"),
          "error"
        );
      }

      return;
    }

    if (decision.isDenied) {
      const rejectionResult = await Swal.fire({
        title: "Reject Order",
        input: "textarea",
        inputLabel: "Rejection reason",
        inputPlaceholder: "Enter rejection reason",
        inputValidator: (value) => {
          if (!String(value || "").trim()) {
            return "Rejection reason is required";
          }

          return undefined;
        },
        showCancelButton: true,
        confirmButtonText: "Reject Order"
      });

      if (!rejectionResult.isConfirmed) return;

      try {
        const response = await axiosInstance.post(
          `/api/lot/${encodeURIComponent(row.LotID)}/inspection`,
          {
            result: "REJECTED",
            rejectionReason: rejectionResult.value.trim()
          }
        );

        await Swal.fire(
          "Rejected",
          response.data?.message || "Order rejected",
          "success"
        );

        await refreshTrackingLists();
      } catch (error) {
        Swal.fire(
          "Error",
          getErrorMessage(error, "Inspection failed"),
          "error"
        );
      }
    }
  };

  /* ================= HISTORY ================= */

  const showOrderHistory = async (row) => {
    try {
      const response = await axiosInstance.get(
        `/api/lot/${encodeURIComponent(row.LotID)}/history`
      );

      const history = response.data?.data || [];

      const historyHtml = history.length
        ? history
            .map(
              (item) => `
                <div class="border-bottom py-2 text-start">
                  <div>
                    <strong>${item.FromStatus || "-"}</strong>
                    →
                    <strong>${item.ToStatus || "-"}</strong>
                  </div>
                  <div class="small text-muted">
                    ${item.ChangedAt
                      ? new Date(item.ChangedAt).toLocaleString("en-IN")
                      : "-"}
                  </div>
                  <div class="small">
                    By: ${item.ChangedBy || "-"}
                  </div>
                  ${
                    item.Remarks
                      ? `<div class="small">Remarks: ${item.Remarks}</div>`
                      : ""
                  }
                </div>
              `
            )
            .join("")
        : `<div class="text-muted">No history found</div>`;

      Swal.fire({
        title: `Order History - ${row.LotID}`,
        html: `
          <div style="max-height: 420px; overflow-y: auto;">
            ${historyHtml}
          </div>
        `,
        width: 700,
        confirmButtonText: "Close"
      });
    } catch (error) {
      Swal.fire(
        "Error",
        getErrorMessage(error, "Unable to load history"),
        "error"
      );
    }
  };

  /* ================= STATUS BADGE ================= */

  const getStatusBadge = (status, isOverdue = false) => {
    const normalizedStatus = normalizeStatus(status);

    let color = "secondary";

    if (normalizedStatus === "NEW SERIES") color = "warning";
    else if (normalizedStatus === "PHOTO NOT FOUND") color = "danger";
    else if (normalizedStatus === "PO RAISED") color = "primary";
    else if (normalizedStatus === "ASSIGNED") color = "info";
    else if (normalizedStatus === "ACKNOWLEDGED") color = "primary";
    else if (normalizedStatus === "IN PROGRESS") color = "warning";
    else if (normalizedStatus === "READY / DISPATCHED") color = "info";
    else if (normalizedStatus === "DELIVERED") color = "primary";
    else if (normalizedStatus === "COMPLETED") color = "success";
    else if (normalizedStatus === "REJECTED") color = "danger";

    return (
      <div className="d-flex flex-column gap-1 align-items-start">
        <span className={`badge bg-${color} text-white premium-badge shadow-sm`}>
          {status || "-"}
        </span>

        {Boolean(isOverdue) && (
          <span className="badge bg-danger premium-badge shadow-sm">
            OVERDUE
          </span>
        )}
      </div>
    );
  };

  /* ================= COMMON PHOTO CELL ================= */

  const renderSamplePhotos = (row) => {
    const photos = parsePhotos(row.SamplePhotos);

    if (!photos.length) {
      return <span>-</span>;
    }

    return (
      <div className="d-flex gap-2 flex-wrap py-2">
        {photos.slice(0, 4).map((image, index) => {
          const imageUrl = buildImageUrl(image);

          return (
            <img
              key={`${image}-${index}`}
              src={imageUrl}
              alt={`Sample ${index + 1}`}
              style={{
                width: 52,
                height: 52,
                objectFit: "cover",
                borderRadius: 6,
                cursor: "pointer",
                border: "1px solid #ddd"
              }}
              onClick={() => {
                window.open(
                  imageUrl,
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            />
          );
        })}

        {photos.length > 4 && (
          <span className="badge bg-secondary align-self-center">
            +{photos.length - 4}
          </span>
        )}
      </div>
    );
  };

  /* ================= BRAND COLUMNS ================= */

  const brandColumns = [
    {
      name: "Lot ID",
      selector: (row) => row.LotID || "-",
      sortable: true
    },
    {
      name: "Brand Name",
      sortable: true,
      selector: (row) => row.Title || "-",
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none fw-semibold"
          onClick={() => handleViewImages(row.PartyID)}
        >
          {row.Title || "-"}
        </button>
      )
    },
    {
      name: "Action",
      minWidth: "180px",
      cell: (row) => (
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light premium-action-btn text-primary border-0 shadow-sm"
            style={{ backgroundColor: '#eff6ff' }}
            onClick={() => handleEdit(row.PartyID)}
          >
            Edit
          </button>

          <button
            type="button"
            className="btn btn-sm btn-light premium-action-btn text-danger border-0 shadow-sm"
            style={{ backgroundColor: '#fef2f2' }}
            onClick={() => handleArchive(row)}
          >
            Archive
          </button>
        </div>
      )
    }
  ];

  /* ================= KALIM COLUMNS ================= */

  const kalimColumns = [
    {
      name: "Lot ID",
      selector: (row) => row.LotID || "-",
      sortable: true,
      minWidth: "220px",
      wrap: true
    },
    {
      name: "Status",
      minWidth: "150px",
      cell: (row) =>
        getStatusBadge(row.Status, row.IsOverdue)
    },
    {
      name: "Action",
      minWidth: "170px",
      cell: (row) => {
        const status = normalizeStatus(row.Status);

        const canRaisePO =
          (
            status === "COMPLETED" ||
            row.PhotoFound === true ||
            row.PhotoFound === 1
          ) &&
          status !== "PO RAISED";

        return (
          <div className="d-flex gap-2">
            {canRaisePO && (
              <button
                type="button"
                className="btn btn-sm premium-action-btn premium-btn-primary shadow-sm text-white"
                onClick={() => raisePurchaseOrder(row.LotID)}
              >
                PO Raised
              </button>
            )}

            {row.BoxOrderNo && (
              <button
                type="button"
                className="btn btn-sm btn-light premium-action-btn border-0 shadow-sm"
                style={{ backgroundColor: '#f1f5f9', color: '#475569' }}
                onClick={() => showOrderHistory(row)}
              >
                History
              </button>
            )}
          </div>
        );
      }
    }
  ];

  /* ================= SUPERVISOR COLUMNS ================= */

  const supervisorColumns = [
    /*
    {
      name: "Order No.",
      selector: (row) => row.BoxOrderNo || "-",
      minWidth: "220px",
      wrap: true
    },
    */
    {
      name: "Lot ID",
      selector: (row) => row.LotID || "-",
      sortable: true,
      minWidth: "220px",
      wrap: true
    },
    /*
    {
      name: "Vendor",
      minWidth: "160px",
      cell: (row) => (
        <div>
          <div>{row.VendorName || "-"}</div>
          <small className="text-muted">
            {row.VendorID || ""}
          </small>
        </div>
      )
    },
    {
      name: "Qty",
      selector: (row) => row.VendorQty || "-",
      center: true
    },
    {
      name: "Due Date",
      selector: (row) => formatDate(row.DueDate),
      minWidth: "120px"
    },
    {
      name: "Photos",
      minWidth: "250px",
      cell: renderSamplePhotos
    },
    */
    {
      name: "Status",
      minWidth: "170px",
      cell: (row) =>
        getStatusBadge(row.Status, row.IsOverdue)
    },
    {
      name: "Action",
      minWidth: "300px",
      cell: (row) => {
        const status = normalizeStatus(row.Status);

        const canAssign =
          (
            row.PhotoFound === false ||
            row.PhotoFound === 0
          ) &&
          ![
            "ASSIGNED",
            "ACKNOWLEDGED",
            "IN PROGRESS",
            "READY / DISPATCHED",
            "DELIVERED"
          ].includes(status);

        return (
          <div className="d-flex gap-2 flex-wrap py-2">
            {canAssign && (
              <button
                type="button"
                className="btn btn-sm btn-warning premium-action-btn shadow-sm text-white"
                style={{ backgroundColor: '#FFB547' }}
                onClick={() => openAssignModal(row)}
              >
                Assign Vendor
              </button>
            )}

            {status === "READY / DISPATCHED" && (
              <button
                type="button"
                className="btn btn-sm btn-primary premium-action-btn shadow-sm text-white"
                style={{ backgroundColor: '#3965FF' }}
                onClick={() => receiveOrder(row)}
              >
                Receive
              </button>
            )}

            {status === "DELIVERED" && (
              <button
                type="button"
                className="btn btn-sm btn-success premium-action-btn shadow-sm text-white"
                style={{ backgroundColor: '#01B574' }}
                onClick={() => inspectOrder(row)}
              >
                Inspect
              </button>
            )}

            {row.BoxOrderNo && (
              <button
                type="button"
                className="btn btn-sm btn-light premium-action-btn border-0 shadow-sm"
                style={{ backgroundColor: '#F4F7FE', color: '#A3AED0' }}
                onClick={() => showOrderHistory(row)}
              >
                History
              </button>
            )}
          </div>
        );
      }
    }
  ];

  /* ================= VENDOR COLUMNS ================= */

  const vendorColumns = [
    {
      name: "Order No.",
      selector: (row) => row.BoxOrderNo || "-",
      minWidth: "220px",
      wrap: true
    },
    {
      name: "Lot ID",
      selector: (row) => row.LotID || "-",
      sortable: true,
      minWidth: "220px",
      wrap: true
    },
    {
      name: "Quantity",
      selector: (row) => row.VendorQty || "-",
      center: true
    },
    {
      name: "Due Date",
      minWidth: "120px",
      selector: (row) => formatDate(row.DueDate)
    },
    {
      name: "Notes",
      minWidth: "180px",
      selector: (row) => row.BoxNotes || "-"
    },
    {
      name: "Sample Photos",
      minWidth: "250px",
      cell: renderSamplePhotos
    },
    {
      name: "Status",
      minWidth: "170px",
      cell: (row) =>
        getStatusBadge(row.Status, row.IsOverdue)
    },
    {
      name: "Action",
      minWidth: "300px",
      cell: (row) => {
        const status = normalizeStatus(row.Status);

        return (
          <div className="d-flex gap-2 flex-wrap py-2">
            {status === "ASSIGNED" && (
              <button
                type="button"
                className="btn btn-sm btn-primary premium-action-btn shadow-sm text-white"
                style={{ backgroundColor: '#3965FF' }}
                onClick={() =>
                  updateVendorStatus(row, "ACKNOWLEDGED")
                }
              >
                Acknowledge
              </button>
            )}

            {status === "ACKNOWLEDGED" && (
              <button
                type="button"
                className="btn btn-sm btn-warning premium-action-btn shadow-sm text-white"
                style={{ backgroundColor: '#FFB547' }}
                onClick={() =>
                  updateVendorStatus(row, "IN PROGRESS")
                }
              >
                Start Work
              </button>
            )}

            {status === "IN PROGRESS" && (
              <button
                type="button"
                className="btn btn-sm btn-info premium-action-btn shadow-sm text-white"
                style={{ backgroundColor: '#00E396' }}
                onClick={() =>
                  updateVendorStatus(
                    row,
                    "READY / DISPATCHED"
                  )
                }
              >
                Ready / Dispatch
              </button>
            )}

            <button
              type="button"
              className="btn btn-sm btn-light premium-action-btn border-0 shadow-sm"
              style={{ backgroundColor: '#F4F7FE', color: '#A3AED0' }}
              onClick={() => showOrderHistory(row)}
            >
              History
            </button>
          </div>
        );
      }
    }
  ];

  /* ================= UI ================= */

  const customTableStyles = {
    headRow: {
      style: {
        backgroundColor: '#F8F9FA',
        borderBottom: '2px solid #E2E8F0',
        fontSize: '13px',
        fontWeight: '700',
        color: '#475569',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        minHeight: '60px',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
      },
    },
    rows: {
      style: {
        fontSize: '15px',
        color: '#2B3674',
        fontWeight: '500',
        minHeight: '68px',
        borderBottom: '1px solid #E9EDF7',
        transition: 'all 0.2s ease',
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#F4F7FE',
          cursor: 'pointer',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
          zIndex: 1,
        },
      },
    },
    cells: {
      style: {
        padding: '16px 20px',
      },
    },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        
        .dashboard-wrapper {
          font-family: 'Plus Jakarta Sans', sans-serif !important;
          background-color: #F4F7FE;
          min-height: 100vh;
        }
        .premium-card {
          background: #ffffff;
          border-radius: 20px;
          box-shadow: 0px 18px 40px rgba(112, 144, 176, 0.12);
          border: none;
        }
        .kpi-card {
          border-radius: 20px;
          overflow: hidden;
          position: relative;
          color: white;
          border: none;
          transition: all 0.3s ease;
        }
        .kpi-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }
        .kpi-1 {
          background: linear-gradient(135deg, #868CFF 0%, #4318FF 100%);
        }
        .kpi-2 {
          background: linear-gradient(135deg, #01B574 0%, #009760 100%);
        }
        .kpi-bg-shape {
          position: absolute;
          right: -20px;
          top: -20px;
          width: 120px;
          height: 120px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }
        .premium-btn {
          border-radius: 12px;
          font-weight: 600;
          padding: 10px 24px;
          transition: all 0.3s;
        }
        .premium-btn-primary {
          background: #4318FF;
          color: white;
          border: none;
        }
        .premium-btn-primary:hover {
          background: #3311DB;
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(67, 24, 255, 0.2);
          color: white;
        }
        .premium-input {
          border-radius: 12px !important;
          background: #F8F9FA !important;
          border: 1px solid #E2E8F0 !important;
          padding: 12px 20px !important;
          font-weight: 500;
          color: #2B3674 !important;
          transition: all 0.3s;
          box-shadow: 0 2px 5px rgba(0,0,0,0.02) !important;
        }
        .premium-input:focus {
          background: #ffffff !important;
          border-color: #4318FF !important;
          box-shadow: 0 0 0 4px rgba(67, 24, 255, 0.15) !important;
        }
        .premium-tab-list {
          background: #F8F9FA;
          border: 1px solid #E2E8F0;
          border-radius: 14px;
          padding: 6px;
          display: inline-flex;
          gap: 6px;
        }
        .premium-tab-item {
          border-radius: 10px;
          font-weight: 600;
          color: #707EAE;
          padding: 12px 28px;
          transition: all 0.3s;
          border: none;
          background: transparent;
        }
        .premium-tab-item:hover:not(.active) {
          background: rgba(67, 24, 255, 0.05);
          color: #4318FF;
        }
        .premium-tab-item.active {
          background: #4318FF;
          color: #ffffff !important;
          box-shadow: 0 6px 20px rgba(67, 24, 255, 0.25);
        }
        .premium-badge {
          padding: 6px 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: 0.5px;
        }
        .premium-title {
          color: #2B3674;
          font-weight: 800;
          font-size: 26px;
        }
        .premium-action-btn {
          border-radius: 8px;
          font-weight: 600;
          padding: 6px 16px;
          transition: all 0.2s;
        }
        .premium-action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.08);
        }
      `}</style>
    <div className="d-flex dashboard dashboard-wrapper">
      <div
        className={
          isSidebarVisible
            ? "sidebar-container"
            : "sidebar-hidden"
        }
      >
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        <Header
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        <main className="main-container p-4">
          <div className="d-flex justify-content-between align-items-center mb-5 pb-3 border-bottom">
            <h3 className="mb-0 premium-title">
              Box Party Wise Dashboard
            </h3>

            <button
              type="button"
              className="btn btn-light premium-btn shadow-sm"
              style={{ backgroundColor: '#ffffff', color: '#4318FF' }}
              disabled={trackingLoading}
              onClick={refreshTrackingLists}
            >
              {trackingLoading ? "Refreshing..." : "↻ Refresh"}
            </button>
          </div>

          {canSeeBrand && (
            <div className="row mb-5">
              <div className="col-md-6 mb-3 mb-md-0">
                <div className="card kpi-card kpi-1 p-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-start" style={{ minHeight: '140px' }}>
                  <div className="kpi-bg-shape"></div>
                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <h6 style={{ opacity: 0.9, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '13px', marginBottom: '8px' }}>Total Active Parties</h6>
                    <h2 style={{ fontWeight: 800, margin: 0, fontSize: '38px' }}>{kpi.total}</h2>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card kpi-card kpi-2 p-4 shadow-sm h-100 d-flex flex-column justify-content-center align-items-start" style={{ minHeight: '140px' }}>
                  <div className="kpi-bg-shape"></div>
                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <h6 style={{ opacity: 0.9, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', fontSize: '13px', marginBottom: '8px' }}>Total Active Images</h6>
                    <h2 style={{ fontWeight: 800, margin: 0, fontSize: '38px' }}>{kpi.images}</h2>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card premium-card">
            <div className="card-header bg-white border-0 pt-4 pb-0 px-4" style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
              <div className="premium-tab-list">
                <ul className="nav nav-pills m-0 flex-nowrap">
                  {canSeeBrand && (
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`premium-tab-item ${activeTab === "brand" ? "active" : ""}`}
                        onClick={() => setActiveTab("brand")}
                      >
                        Brand Library
                      </button>
                    </li>
                  )}

                  {canSeeKalim && (
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`premium-tab-item ${activeTab === "kalim" ? "active" : ""}`}
                        onClick={() => setActiveTab("kalim")}
                      >
                        Lot Processing
                      </button>
                    </li>
                  )}

                  {canSeeSupervisor && (
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`premium-tab-item ${activeTab === "supervisor" ? "active" : ""}`}
                        onClick={() => setActiveTab("supervisor")}
                      >
                        Vendor Review
                      </button>
                    </li>
                  )}

                  {canSeeVendor && (
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`premium-tab-item ${activeTab === "vendor" ? "active" : ""}`}
                        onClick={() => setActiveTab("vendor")}
                      >
                        Vendor Orders
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="card-body p-4">
              {/* ================= BRAND TAB ================= */}

              {canSeeBrand && activeTab === "brand" && (
                <>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                    <div className="position-relative" style={{ flexGrow: 1, maxWidth: 350 }}>
                      <input
                        className="form-control premium-input shadow-sm"
                        placeholder="🔍 Search brand or lot ID..."
                        value={search}
                        onChange={(event) => {
                          setSearch(event.target.value);
                          setPage(1);
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      className="btn premium-btn premium-btn-primary shadow-sm"
                      onClick={() => {
                        resetForm();
                        setShowModal(true);
                      }}
                    >
                      + Add Brand
                    </button>
                  </div>

                  <DataTable
                    columns={brandColumns}
                    data={records}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={perPage}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    onChangePage={(newPage) => {
                      setPage(newPage);
                    }}
                    onChangeRowsPerPage={(newPerPage) => {
                      setPerPage(newPerPage);
                      setPage(1);
                    }}
                    persistTableHead
                    highlightOnHover
                    responsive
                    customStyles={customTableStyles}
                  />
                </>
              )}

              {/* ================= KALIM TAB ================= */}

              {canSeeKalim && activeTab === "kalim" && (
                <>
                  <div className="d-flex gap-3 mb-4 flex-wrap">
                    <input
                      className="form-control premium-input shadow-sm"
                      placeholder="🔍 Search lot..."
                      value={kalimSearch}
                      onChange={(event) => setKalimSearch(event.target.value)}
                      style={{ maxWidth: 350 }}
                    />
                    <select
                      className="form-select premium-input shadow-sm"
                      value={kalimStatusFilter}
                      onChange={(e) => setKalimStatusFilter(e.target.value)}
                      style={{ maxWidth: 250 }}
                    >
                      <option value="">All Statuses</option>
                      {uniqueKalimStatuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <DataTable
                    columns={kalimColumns}
                    data={filteredKalim}
                    progressPending={trackingLoading}
                    pagination
                    persistTableHead
                    highlightOnHover
                    responsive
                    customStyles={customTableStyles}
                  />
                </>
              )}

              {/* ================= SUPERVISOR TAB ================= */}

              {canSeeSupervisor &&
                activeTab === "supervisor" && (
                  <>
                    <div className="mb-4">
                      <input
                        className="form-control premium-input shadow-sm"
                        placeholder="🔍 Search vendor order..."
                        value={supervisorSearch}
                        onChange={(event) => setSupervisorSearch(event.target.value)}
                        style={{ maxWidth: 350 }}
                      />
                    </div>

                    <DataTable
                      columns={supervisorColumns}
                      data={filteredSupervisor}
                      progressPending={trackingLoading}
                      pagination
                      persistTableHead
                      highlightOnHover
                      responsive
                      customStyles={customTableStyles}
                    />
                  </>
                )}

              {/* ================= VENDOR TAB ================= */}

              {canSeeVendor && activeTab === "vendor" && (
                <>
                  <div className="d-flex gap-3 mb-4 flex-wrap">
                    <input
                      className="form-control premium-input shadow-sm"
                      placeholder="🔍 Search order..."
                      value={vendorSearch}
                      onChange={(event) => setVendorSearch(event.target.value)}
                      style={{ maxWidth: 350 }}
                    />
                    <select
                      className="form-select premium-input shadow-sm"
                      value={vendorStatusFilter}
                      onChange={(e) => setVendorStatusFilter(e.target.value)}
                      style={{ maxWidth: 250 }}
                    >
                      <option value="">All Statuses</option>
                      {uniqueVendorStatuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <DataTable
                    columns={vendorColumns}
                    data={filteredVendor}
                    progressPending={trackingLoading}
                    pagination
                    persistTableHead
                    highlightOnHover
                    responsive
                    customStyles={customTableStyles}
                  />
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* ================= ADD / EDIT BRAND MODAL ================= */}

      {showModal && (
        <>
          <div className="modal-backdrop fade show" />

          <div className="modal show d-block">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editMode
                      ? `Edit Brand: ${selectedTitle}`
                      : "Add Brand"}
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={resetForm}
                  />
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">
                        Brand Name
                      </label>

                      <input
                        className="form-control"
                        required
                        value={formData.title}
                        onChange={(event) =>
                          setFormData((previous) => ({
                            ...previous,
                            title: event.target.value
                          }))
                        }
                      />
                    </div>

                    <div
                      className={`border rounded p-4 text-center ${
                        dragActive
                          ? "bg-light border-primary"
                          : ""
                      }`}
                      style={{
                        borderStyle: "dashed",
                        cursor: "pointer"
                      }}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onClick={() =>
                        document
                          .getElementById("boxImageInput")
                          ?.click()
                      }
                    >
                      <h6>Drag and Drop Images Here</h6>

                      <div className="text-muted">
                        Or click to browse
                      </div>

                      <input
                        id="boxImageInput"
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.webp"
                        hidden
                        onChange={(event) => {
                          handleImageChange(event.target.files);
                          event.target.value = "";
                        }}
                      />

                      <div className="mt-2 text-muted">
                        Maximum {MAX_IMAGES} images
                      </div>
                    </div>

                    {editMode &&
                      existingImages.length > 0 && (
                        <>
                          <h6 className="mt-4">
                            Existing Images
                          </h6>

                          <div className="row">
                            {existingImages.map((image) => (
                              <div
                                className="col-md-3 mb-3"
                                key={image.ImageID}
                              >
                                <div className="position-relative">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute"
                                    style={{
                                      top: 5,
                                      right: 5,
                                      zIndex: 2
                                    }}
                                    onClick={() =>
                                      removeExistingImage(image)
                                    }
                                  >
                                    ✕
                                  </button>

                                  <img
                                    src={buildImageUrl(
                                      image.ImagePath
                                    )}
                                    alt="Existing"
                                    className="img-fluid rounded"
                                    style={{
                                      width: "100%",
                                      height: 120,
                                      objectFit: "cover"
                                    }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                    {formData.newImages.length > 0 && (
                      <>
                        <h6 className="mt-4">
                          New Images
                        </h6>

                        <div className="row">
                          {formData.newImages.map(
                            (file, index) => (
                              <div
                                className="col-md-3 mb-3"
                                key={`${file.name}-${index}`}
                              >
                                <div className="position-relative">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute"
                                    style={{
                                      top: 5,
                                      right: 5,
                                      zIndex: 2
                                    }}
                                    onClick={() =>
                                      removeNewImage(index)
                                    }
                                  >
                                    ✕
                                  </button>

                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={file.name}
                                    className="img-fluid rounded"
                                    style={{
                                      width: "100%",
                                      height: 120,
                                      objectFit: "cover"
                                    }}
                                  />
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={saving}
                    >
                      {saving
                        ? "Saving..."
                        : editMode
                          ? "Update"
                          : "Save"}
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={saving}
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================= VIEW BRAND IMAGES MODAL ================= */}

      {showImageModal && (
        <>
          <div className="modal-backdrop fade show" />

          <div className="modal show d-block">
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Party Images: {selectedTitle}
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() =>
                      setShowImageModal(false)
                    }
                  />
                </div>

                <div className="modal-body">
                  {selectedImages.length === 0 ? (
                    <div className="text-muted text-center py-4">
                      No images found
                    </div>
                  ) : (
                    <div className="row">
                      {selectedImages.map((image) => (
                        <div
                          className="col-md-3 mb-3"
                          key={image.ImageID}
                        >
                          <img
                            src={buildImageUrl(
                              image.ImagePath
                            )}
                            alt="Brand"
                            className="img-fluid rounded"
                            style={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                              cursor: "pointer"
                            }}
                            onClick={() =>
                              window.open(
                                buildImageUrl(
                                  image.ImagePath
                                ),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ================= ASSIGN VENDOR MODAL ================= */}

      {assignModal && (
        <>
          <div className="modal-backdrop fade show" />

          <div className="modal show d-block">
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Assign Vendor
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    disabled={assignSaving}
                    onClick={closeAssignModal}
                  />
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Lot ID
                      </label>

                      <input
                        type="text"
                        className="form-control premium-input bg-light text-muted"
                        value={assignData.lotId}
                        disabled
                        readOnly
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Required Quantity
                      </label>

                      <input
                        type="number"
                        className="form-control premium-input"
                        value={assignData.quantity}
                        onChange={(event) =>
                          setAssignData((previous) => ({
                            ...previous,
                            quantity: event.target.value
                          }))
                        }
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Vendor ID
                      </label>

                      <input
                        type="text"
                        className="form-control premium-input"
                        placeholder="Enter Vendor Employee ID"
                        value={assignData.vendorId}
                        onChange={(event) =>
                          setAssignData((previous) => ({
                            ...previous,
                            vendorId: event.target.value
                          }))
                        }
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Vendor Name
                      </label>

                      <input
                        type="text"
                        className="form-control premium-input"
                        placeholder="Enter Vendor Name"
                        value={assignData.vendorName}
                        onChange={(event) =>
                          setAssignData((previous) => ({
                            ...previous,
                            vendorName: event.target.value
                          }))
                        }
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Due Date
                      </label>

                      <input
                        type="date"
                        min={getMinimumDueDate()}
                        className="form-control premium-input"
                        value={assignData.dueDate}
                        onChange={(event) =>
                          setAssignData((previous) => ({
                            ...previous,
                            dueDate: event.target.value
                          }))
                        }
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Photo Source
                      </label>

                      <div className="d-flex premium-tab-list w-100">
                        <button
                          type="button"
                          className={`premium-tab-item ${
                            assignData.photoSource === "GROUP" ? "active" : ""
                          }`}
                          onClick={() => handlePhotoSourceChange("GROUP")}
                          style={{ flex: 1, textAlign: "center", padding: "8px 10px", fontSize: "14px" }}
                        >
                          Saved Group
                        </button>

                        <button
                          type="button"
                          className={`premium-tab-item ${
                            assignData.photoSource === "UPLOAD" ? "active" : ""
                          }`}
                          onClick={() => handlePhotoSourceChange("UPLOAD")}
                          style={{ flex: 1, textAlign: "center", padding: "8px 10px", fontSize: "14px" }}
                        >
                          Upload
                        </button>
                      </div>
                    </div>

                    <div className="col-12 mb-3 mt-2">
                      <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                        Notes
                      </label>

                      <textarea
                        className="form-control premium-input"
                        rows="3"
                        placeholder="Optional instructions for vendor"
                        value={assignData.notes}
                        onChange={(event) =>
                          setAssignData((previous) => ({
                            ...previous,
                            notes: event.target.value
                          }))
                        }
                      />
                    </div>
                  </div>

                  {assignData.photoSource === "GROUP" && (
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                          Select Photo Group
                        </label>

                        <select
                          className="form-select premium-input"
                          value={assignData.photoGroupId}
                          onChange={(event) =>
                            handlePhotoGroupChange(
                              event.target.value
                            )
                          }
                        >
                          <option value="">
                            -- Select Photo Group --
                          </option>

                          {brandOptions.map((brand) => (
                            <option
                              key={brand.PartyID}
                              value={brand.PartyID}
                            >
                              {brand.Title} ({brand.LotID})
                            </option>
                          ))}
                        </select>
                      </div>

                      {groupImagesLoading && (
                        <div className="text-muted">
                          Loading group images...
                        </div>
                      )}

                      {!groupImagesLoading &&
                        selectedGroupImages.length > 0 && (
                          <div className="row">
                            {selectedGroupImages.map(
                              (image) => (
                                <div
                                  className="col-md-2 mb-3"
                                  key={image.ImageID}
                                >
                                  <img
                                    src={buildImageUrl(
                                      image.ImagePath
                                    )}
                                    alt="Group"
                                    className="img-fluid rounded"
                                    style={{
                                      width: "100%",
                                      height: 110,
                                      objectFit: "cover"
                                    }}
                                  />
                                </div>
                              )
                            )}
                          </div>
                        )}
                    </>
                  )}

                  {assignData.photoSource === "UPLOAD" && (
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-semibold text-secondary small text-uppercase tracking-wider">
                          Upload Sample Photos
                        </label>

                        <input
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.webp"
                          className="form-control premium-input"
                          onChange={(event) => {
                            handleAssignImages(
                              event.target.files
                            );

                            event.target.value = "";
                          }}
                        />
                      </div>

                      {assignData.images.length > 0 && (
                        <div className="row">
                          {assignData.images.map(
                            (image, index) => (
                              <div
                                className="col-md-3 mb-3"
                                key={`${image.name}-${index}`}
                              >
                                <div className="position-relative">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger position-absolute"
                                    style={{
                                      top: 5,
                                      right: 5,
                                      zIndex: 2
                                    }}
                                    onClick={() =>
                                      removeAssignImage(index)
                                    }
                                  >
                                    ✕
                                  </button>

                                  <img
                                    src={URL.createObjectURL(
                                      image
                                    )}
                                    alt={image.name}
                                    className="img-fluid rounded"
                                    style={{
                                      width: "100%",
                                      height: 120,
                                      objectFit: "cover"
                                    }}
                                  />
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={assignSaving}
                    onClick={submitAssignVendor}
                  >
                    {assignSaving
                      ? "Assigning..."
                      : "Assign Vendor"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={assignSaving}
                    onClick={closeAssignModal}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
}